import { encodeFile } from '@acme/common'
import {
    Grid,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { FormField, FormFieldCheckbox, FormFile } from 'global/components/Form'
import FormFieldToggleButtonGroup from 'global/components/Form/ToggleButtonGroup'
import { SelectEmpresa } from 'global/components/Selects'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { useCallback, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { afd } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'

type FormData = {
    afd: File
    dataInicial: string
    dataFinal: string
    tipoDocumento: 'cpf' | 'pis'
    isPortaria671?: boolean
}

export function ImportAfd() {
    const companyId = useStore(state => state.companyId)
    const navigate = useNavigate()
    const [isCpfOnly, setCpfOnly] = useState(true)

    const { mutateAsync } = afd.mutations.useImport()

    const handleSubmit = useCallback<SubmitHandler<FormData>>(
        async ({
            afd: file,
            dataInicial,
            dataFinal,
            tipoDocumento,
            isPortaria671,
        }) => {
            if (!file) return toast.error('Selecione um arquivo para importar')

            const data = {
                file: await encodeFile(file),
                dataInicial,
                dataFinal,
                empresaId: companyId,
                ...(isPortaria671
                    ? { isPortaria671: true, tipoDocumento: 'cpf' }
                    : { tipoDocumento }),
            }

            await mutateAsync(data as any, {
                onSuccess: historico => {
                    afd.fillHistoricos(historico)
                    toast.success('Arquivo importado com sucesso!')

                    navigate(-1)
                },
                onError: err => {
                    toast.error(err.message)
                },
            })
        },
        [companyId],
    )

    return (
        <Dialog.Root
            width="34rem"
            height="auto"
            open={hasPermission(['create', 'AFD'])}
            onSubmit={handleSubmit}
            defaultValues={{
                dataInicial: dayjs().startOf('month').format('YYYY-MM-DD'),
                dataFinal: dayjs().endOf('month').format('YYYY-MM-DD'),
                tipoDocumento: 'cpf',
                isPortaria671: true,
            }}
        >
            <Dialog.Title>
                <div
                    style={{
                        display: 'inline-flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'center',
                    }}
                >
                    <span>Importar AFD</span>
                    <div style={{ flex: 1 }}></div>
                    <div style={{ width: 'fit-content' }}>
                        {isCpfOnly ? (
                            <ToggleButtonGroup
                                value="cpf"
                                exclusive
                                title="Portaria 671 exige CPF"
                            >
                                <ToggleButton value="cpf">CPF</ToggleButton>
                            </ToggleButtonGroup>
                        ) : (
                            <FormFieldToggleButtonGroup
                                title="Tipo de documento contido no AFD"
                                name="tipoDocumento"
                                disabled={isCpfOnly}
                                options={[
                                    { label: 'CPF', value: 'cpf' },
                                    { label: 'PIS', value: 'pis' },
                                ]}
                            />
                        )}
                    </div>
                </div>
            </Dialog.Title>
            <Dialog.Content dividers>
                <Grid container columnSpacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <SelectEmpresa width="100%" size="medium" />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography mb={1.5} mt={3}>
                            Periodo de importação do registro
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <FormField
                            name="dataInicial"
                            label="Inicio"
                            type="date"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormField name="dataFinal" label="Final" type="date" />
                    </Grid>
                    <Grid item xs={12}>
                        <FormFieldCheckbox
                            name="isPortaria671"
                            label="Utiliza padrão de layout da portaria 671"
                            onChange={(_, v) => setCpfOnly(!v)}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <FormFile name="afd" notPreview accept="text/plain" />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton>
                    Importar
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}
